import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/store", label: "Storefront" },
  { to: "/#network", label: "Network" },
  { to: "/#alliance", label: "FinBPM Alliance" },
  { to: "/portal", label: "Corridor Portal" },
  { to: "/contact", label: "Contact" },
];

export function Nav() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const dash = user?.role === "admin" ? "/admin" : "/portal";

  return (
    <header className="fixed top-0 inset-x-0 z-50 print:hidden" data-testid="site-header">
      <div className="mx-auto mt-3 max-w-[1400px] px-3 sm:px-6">
        <div className="flex items-center justify-between rounded-full border border-white/10 bg-[#0b111c]/75 px-4 py-2.5 backdrop-blur-xl sm:px-6">
          <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-slate-300 transition-colors duration-200 hover:text-white">
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => setOpen(true)} data-testid="nav-cart-button"
              className="relative grid h-10 w-10 place-items-center rounded-full border border-white/10 transition-colors hover:border-sky-400">
              <ShoppingBag className="h-4 w-4" />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[var(--gold)] px-1 font-mono text-[10px] font-semibold text-black">{count}</span>
              )}
            </button>
            <Link to={user ? dash : "/login"} data-testid="nav-signin-link"
              className="hidden text-sm text-slate-300 hover:text-white sm:block">{user ? "Dashboard" : "Sign in"}</Link>
            <Link to="/apply" data-testid="nav-apply-button" className="btn-primary !px-4 !py-2.5 !text-sm">Apply</Link>
            <button className="grid h-10 w-10 place-items-center lg:hidden" onClick={() => setMobile(true)} data-testid="nav-mobile-open">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#080B11]/95 backdrop-blur-xl lg:hidden" data-testid="mobile-menu">
            <div className="flex justify-end p-6">
              <button onClick={() => setMobile(false)} data-testid="nav-mobile-close"><X className="h-6 w-6" /></button>
            </div>
            <div className="flex flex-col gap-6 px-8 pt-6">
              {[...LINKS, { to: user ? dash : "/login", label: user ? "Dashboard" : "Sign in" }, { to: "/apply", label: "Apply for processing" }].map((l, i) => (
                <motion.div key={l.to + l.label} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <Link to={l.to} onClick={() => setMobile(false)} className="font-display text-4xl font-medium"
                    data-testid={`mobile-link-${i}`}>{l.label}</Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
